import { useRef } from "react";
import { useGSAP } from "@gsap/react";
import { gsap } from "gsap";
import { AustraliaDots, Orbit, Target, TrendChart } from "../shared";
import { ScrollItem } from "./scroll-item";
import Image from "next/image";
import { useSectionNavTheme } from "@/app/hooks/useSectionNavTheme";

export const HorizontalScroll = () => {
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const scrollItemsRef = useRef<(HTMLDivElement | null)[]>([]);
  const satellite1Ref = useRef<HTMLImageElement | null>(null);

  useSectionNavTheme({
    trigger: ".horizontal-scroll",
    isDark: false,
    end: "200% 40px",
  });

  useGSAP(() => {
    const mm = gsap.matchMedia();

    mm.add("(min-width: 768px)", () => {
      const section = sectionRef.current;
      const content = contentRef.current;
      const scrollItems = scrollItemsRef.current.filter(Boolean);

      if (!section || !content || !scrollItems.length) return;

      // Detect mobile devices
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

      // Calculate total width needed for horizontal scroll
      const gap = parseFloat(getComputedStyle(content).gap) || 0;
      const totalWidth =
        scrollItems.length * (scrollItems[0]?.offsetWidth ?? 0) +
        gap * (scrollItems.length - 1);
      const containerWidth = containerRef.current?.offsetWidth || 0;
      const scrollDistance = totalWidth - containerWidth;

      const satellite1Tl = gsap.timeline({
        ease: "none",
        scrollTrigger: {
          trigger: section,
          start: "top top",
          end: "+=500%",
          scrub: isMobile ? 0.5 : 1, // Faster scrub on mobile
          // markers: true,
          invalidateOnRefresh: true,
        },
      });
      satellite1Tl.to(satellite1Ref.current, {
        y: scrollDistance * 1.5,
        x: "90vw",
        rotation: 360,
        force3D: true, // Enable hardware acceleration
      });

      // Create horizontal scroll timeline
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: "top top",
          end: () => `+=${scrollDistance}`,
          scrub: isMobile ? 0.5 : 1, // Faster scrub on mobile for less lag
          pin: content,
          anticipatePin: isMobile ? 0 : 1, // Disable anticipatePin on mobile to prevent jitter
          invalidateOnRefresh: true,
          pinSpacing: true,
          fastScrollEnd: isMobile ? true : false, // Improve mobile scroll end detection
          // markers: true,
          onUpdate: (self) => {
            // Animate progress bars based on scroll progress
            const progress = self.progress;

            // Update progress bars for each scroll item
            scrollItems.forEach((item, index) => {
              const progressBar = item?.querySelector(".progress-bar");
              if (progressBar) {
                const itemProgress = Math.max(
                  0,
                  Math.min(1, progress * scrollItems.length - index)
                );
                gsap.set(progressBar, {
                  clipPath: `polygon(0% 0%, ${itemProgress * 100}% 0%, ${
                    itemProgress * 100
                  }% 100%, 0% 100%)`,
                  force3D: true,
                });
              }
            });
          },
        },
      });

      // Animate horizontal movement
      tl.fromTo(
        scrollItems,
        { x: 0 },
        {
          x: -scrollDistance,
          duration: 1,
          ease: "none",
          force3D: true, // Enable hardware acceleration
        }
      );
    });
  }, []);

  return (
    <section ref={sectionRef} className="relative horizontal-scroll py-10">
      <Image
        src="/images/blob.svg"
        alt="Background Blob"
        height={750}
        width={830}
        className="absolute top-0 -left-[15vw]"
        draggable={false}
      />
      <div ref={containerRef} className="container">
        <div
          ref={contentRef}
          className="horizontal-scroll__content flex flex-col md:flex-row gap-8 py-[20vh] md:py-[15vh]"
        >
          <Image
            ref={satellite1Ref}
            src="/images/satellite1.svg"
            alt="Background Satellite"
            height={350}
            width={430}
            className="absolute top-0 -left-[5vw] opacity-75"
            draggable={false}
          />
          <ScrollItem
            ref={(el) => {
              scrollItemsRef.current[0] = el;
            }}
            title="Ready-to-Use Satellite Data"
            description="AI processing removes atmospheric interference and image noise, delivering clean data without manual correction."
          >
            <Orbit className="w-24 h-24 md:w-30 md:h-30 lg:w-36 lg:h-36 mx-auto" />
          </ScrollItem>

          <ScrollItem
            ref={(el) => {
              scrollItemsRef.current[1] = el;
            }}
            title="Mass Screening"
            description="Simultaneous analysis across thousands of properties to identify potential issues."
          >
            <AustraliaDots className="w-24 h-24 md:w-30 md:h-30 lg:w-36 lg:h-36 mx-auto" />
          </ScrollItem>

          <ScrollItem
            ref={(el) => {
              scrollItemsRef.current[2] = el;
            }}
            title="Historical Tracking"
            description="Analysis extending back to 2016-17 for trend identification."
          >
            <TrendChart className="w-24 h-24 md:w-30 md:h-30 lg:w-36 lg:h-36 mx-auto" />
          </ScrollItem>

          <ScrollItem
            ref={(el) => {
              scrollItemsRef.current[3] = el;
            }}
            title="User-Friendly Interface"
            description="Compliance officers can operate the system without remote sensing expertise."
          >
            <Target className="w-24 h-24 md:w-30 md:h-30 lg:w-36 lg:h-36 mx-auto" />
          </ScrollItem>
        </div>
      </div>
    </section>
  );
};
